import React, { useCallback, useEffect, useState } from "react";
import { Button, Input, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import {
    fetchPosValorConfig,
    savePosValorConfig,
} from "../../api/requests/pos-reconciliation-requests";

/* ============================================================================
 * Valör (T+X iş günü) ayarları — firma × POS sağlayıcı.
 *
 * Firma yöneticisi kendi firmasının valörünü Ayarlar → Sanal POS Entegrasyon
 * ekranından düzenler; buradaki kopya süper admin içindir: firmayı seçip onun
 * ayarını düzenleyebilsin. İkisi de aynı uca yazar, kapsam kontrolü sunucuda.
 *
 * Bir firma birden fazla POS ile çalışabildiği için tüm sağlayıcılar listelenir.
 * Boş bırakılan sağlayıcıda valör HESAPLANMAZ: POS Rapor'un valör kolonu "—"
 * kalır — 0 (T+0) ile karıştırılmasın diye kayıt silinir, sıfır yazılmaz.
 * ========================================================================== */

const NAVY = "#0A316E";
const MAX_VALOR_DAYS = 60;   // sunucudaki PosValorConfigDTO.MAX_VALOR_DAYS ile aynı

/** 0 → "T+0 (aynı gün)" · 2 → "T+2 gün" */
const valorLabel = (days) => {
    const value = Number(days);
    if (!Number.isFinite(value)) return null;
    return value === 0 ? "T+0 (aynı gün)" : `T+${value} gün`;
};

export default function CompanyValorSettings({ companyId }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [providers, setProviders] = useState([]);
    // { [virtualPos]: "2" } — boş string "valör girilmemiş" demektir
    const [days, setDays] = useState({});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetchPosValorConfig(companyId);
            if (res?.statusCode === 200 && Array.isArray(res.data)) {
                setProviders(res.data.map((p) => ({ key: p.virtualPos, label: p.providerLabel || p.virtualPos })));
                const map = {};
                res.data.forEach((p) => {
                    map[p.virtualPos] = p.valorDays != null ? String(p.valorDays) : "";
                });
                setDays(map);
            } else {
                toast.error(res?.message || "Valör ayarları alınamadı");
            }
        } catch (e) {
            toast.error("Valör ayarları yüklenirken hata oluştu");
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    useEffect(() => {
        load();
    }, [load]);

    const handleSave = async () => {
        const invalid = providers.find((p) => {
            const raw = days[p.key];
            if (raw === "" || raw == null) return false;
            const value = Number(raw);
            return !Number.isInteger(value) || value < 0 || value > MAX_VALOR_DAYS;
        });
        if (invalid) {
            toast.error(`${invalid.label}: valör 0 ile ${MAX_VALOR_DAYS} arasında tam sayı olmalıdır`);
            return;
        }

        setSaving(true);
        try {
            const items = providers.map((p) => ({
                virtualPos: p.key,
                // Boş bırakılan alan kaydı siler; 0 ise T+0 olarak kaydedilir.
                valorDays: days[p.key] === "" || days[p.key] == null ? null : Number(days[p.key]),
            }));
            const res = await savePosValorConfig(companyId, items);
            if (res?.statusCode === 200) {
                toast.success("Valör ayarları kaydedildi");
                await load();
            } else {
                toast.error(res?.message || "Valör ayarları kaydedilemedi");
            }
        } catch (e) {
            toast.error("Kaydederken hata oluştu");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex align-items-center gap-2 text-muted p-3">
                <Spinner size="sm" /> <span>Valör ayarları yükleniyor...</span>
            </div>
        );
    }

    const definedCount = providers.filter((p) => days[p.key] !== "" && days[p.key] != null).length;

    return (
        <div>
            <div className="d-flex align-items-center justify-content-between mb-1 flex-wrap gap-2">
                <h5 className="text-muted mb-0">Valör (Yatış) Ayarları</h5>
                <Button
                    size="sm"
                    style={{ backgroundColor: NAVY, borderColor: NAVY, color: "#fff", borderRadius: 999 }}
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? "Kaydediliyor..." : "Kaydet"}
                </Button>
            </div>
            <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                Bu firmanın her POS sağlayıcısı için paranın kaç <strong>gün</strong> sonra
                hesaba geçtiğini girin. Yatış tarihi işlem tarihine bu kadar takvim günü (hafta
                sonları dahil) eklenerek bulunur; sonuç hafta sonuna ya da resmî tatile denk
                gelirse bir sonraki iş gününe kayar. Boş bırakılan sağlayıcıda POS
                Rapor'un <em>valör</em> kolonu "—" görünür (0 ile karıştırılmaması için).
                {definedCount > 0 && ` Şu an ${definedCount} sağlayıcıda valör tanımlı.`}
            </p>

            {/* Sanal POS entegrasyon kartlarıyla aynı grid deseni */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                    gap: "1rem",
                }}
            >
                {providers.map((p) => {
                    const value = days[p.key];
                    const hasValor = value !== "" && value != null;
                    const hint = hasValor ? valorLabel(value) : null;
                    return (
                        <div
                            key={p.key}
                            className="h-100 d-flex flex-column border-0 shadow-sm"
                            style={{
                                borderRadius: 16,
                                overflow: "hidden",
                                background: hasValor
                                    ? "linear-gradient(180deg, rgba(10,49,110,.06), rgba(10,49,110,0))"
                                    : "#fff",
                            }}
                        >
                            <div
                                style={{
                                    height: 4,
                                    background: hasValor ? `linear-gradient(90deg, ${NAVY}, #3b6bbf)` : "#e7eaf0",
                                }}
                            />

                            <div className="p-3 d-flex flex-column flex-grow-1">
                                <div className="d-flex justify-content-between align-items-start">
                                    <div
                                        className="d-flex align-items-center justify-content-center bg-white shadow-sm"
                                        style={{ width: 48, height: 48, borderRadius: 14, flexShrink: 0 }}
                                    >
                                        <i className="bx bx-calendar-check" style={{ fontSize: 24, color: NAVY }} />
                                    </div>
                                    <span
                                        className="d-inline-flex align-items-center fw-semibold"
                                        style={{
                                            color: hasValor ? NAVY : "#5c6a86",
                                            backgroundColor: hasValor ? "rgba(10,49,110,.12)" : "rgba(92,106,134,.12)",
                                            borderRadius: 999,
                                            padding: ".25rem .7rem",
                                            fontSize: 12,
                                        }}
                                    >
                                        {hasValor ? `T+${value}` : "Tanımsız"}
                                    </span>
                                </div>

                                <h6 className="fw-bold mt-3 mb-1" style={{ color: "#1f2937" }}>
                                    {p.label}
                                </h6>
                                <p className="text-muted mb-3" style={{ fontSize: 13, lineHeight: 1.35 }}>
                                    Paranın hesaba geçme süresi
                                </p>

                                <div className="mt-auto">
                                    <div className="input-group input-group-sm">
                                        <span className="input-group-text">T+</span>
                                        <Input
                                            type="number"
                                            min={0}
                                            max={MAX_VALOR_DAYS}
                                            step="1"
                                            value={value ?? ""}
                                            onChange={(e) =>
                                                setDays((prev) => ({ ...prev, [p.key]: e.target.value }))
                                            }
                                            placeholder="örn. 1"
                                        />
                                        <span className="input-group-text">gün</span>
                                    </div>
                                    <div className="text-muted mt-1" style={{ fontSize: 11, minHeight: 16 }}>
                                        {hint || "valör girilmedi"}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
